import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Spinner } from "@/components/ui/Spinner";
import {
  loadDocumentPaperState,
  saveDocumentPaperState,
  setPagePaperOverride,
  type DocumentPaperState,
} from "@/lib/documentPaperOverrides";
import {
  classifyPagePts,
  type DinPaperSize,
  type PaperOrientation,
} from "@/lib/paperSize";
import { PaperSizeBar } from "@/components/pdf/PaperSizeBar";

type PdfJsModule = typeof import("pdfjs-dist");
type PdfDocument = Awaited<ReturnType<PdfJsModule["getDocument"]>["promise"]>;

type PageMeta = {
  pageNum: number;
  size: DinPaperSize | null;
  orientation: PaperOrientation;
};

/**
 * Rendert ein PDF seitenweise per PDF.js (Canvas).
 * Lazy laden – vor allem für iOS/iPad, wo iframe+blob weiß bleibt.
 */
export function PdfJsPages({
  blob,
  title = "PDF",
  className,
  paperKey,
  showPaperSize = false,
}: {
  blob: Blob;
  title?: string;
  className?: string;
  /** localStorage-Schlüssel für erkannte/überschriebene Papierformate. */
  paperKey?: string;
  showPaperSize?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState<PageMeta[]>([]);
  const [pdf, setPdf] = useState<PdfDocument | null>(null);
  const [cssWidth, setCssWidth] = useState(0);
  const [paperState, setPaperState] = useState<DocumentPaperState>({
    detected: [],
    overrides: {},
  });

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return;
    const apply = () => {
      setCssWidth(
        Math.max(280, Math.floor(host.clientWidth || window.innerWidth || 320)),
      );
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let pdfDoc: PdfDocument | null = null;

    async function run() {
      setLoading(true);
      setError(null);
      setPages([]);
      setPdf(null);

      try {
        const pdfjs = await import("pdfjs-dist");
        const workerSrc = (
          await import("pdfjs-dist/build/pdf.worker.min.mjs?url")
        ).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

        const data = new Uint8Array(await blob.arrayBuffer());
        const loadingTask = pdfjs.getDocument({ data });
        const nextPdf = await loadingTask.promise;
        pdfDoc = nextPdf;
        if (cancelled) {
          await nextPdf.destroy();
          return;
        }

        const nextPages: PageMeta[] = [];
        for (let pageNum = 1; pageNum <= nextPdf.numPages; pageNum += 1) {
          const page = await nextPdf.getPage(pageNum);
          const unscaled = page.getViewport({ scale: 1 });
          // getViewport liefert bereits rotierte Maße.
          const classified = classifyPagePts(unscaled.width, unscaled.height);
          nextPages.push({
            pageNum,
            size: classified.size,
            orientation: classified.orientation,
          });
        }

        const stored = paperKey ? loadDocumentPaperState(paperKey) : null;
        const detected = nextPages.map((page) => page.size);
        const nextState: DocumentPaperState = {
          detected,
          overrides: stored?.overrides ?? {},
        };
        if (paperKey) saveDocumentPaperState(paperKey, nextState);

        if (cancelled) {
          await nextPdf.destroy();
          return;
        }
        setPaperState(nextState);
        setPages(nextPages);
        setPdf(nextPdf);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "PDF konnte nicht angezeigt werden.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void run();

    return () => {
      cancelled = true;
      if (pdfDoc) {
        void pdfDoc.destroy();
      }
    };
  }, [blob, paperKey]);

  function handlePaperChange(pageNum: number, size: DinPaperSize) {
    setPaperState((prev) => {
      const next = setPagePaperOverride(prev, pageNum, size);
      if (paperKey) saveDocumentPaperState(paperKey, next);
      return next;
    });
  }

  return (
    <div className={clsx("relative min-h-[12rem]", className)}>
      {loading ? (
        <div className="flex min-h-[12rem] items-center justify-center py-10">
          <Spinner />
        </div>
      ) : null}
      {error ? (
        <p className="px-4 py-8 text-center text-sm text-status-red">{error}</p>
      ) : null}
      <div
        ref={containerRef}
        className={clsx(
          "mx-auto w-full max-w-full overflow-x-hidden",
          loading && "invisible absolute inset-0",
        )}
      >
        {pdf && cssWidth > 0
          ? pages.map((page) => (
              <figure key={page.pageNum} className="mb-4 last:mb-0">
                {showPaperSize ? (
                  <PaperSizeBar
                    pageNum={page.pageNum}
                    pageCount={pages.length}
                    detected={page.size}
                    orientation={page.orientation}
                    value={paperState.overrides[page.pageNum] ?? null}
                    onChange={(size) => handlePaperChange(page.pageNum, size)}
                  />
                ) : null}
                <PdfJsPageCanvas
                  pdf={pdf}
                  pageNum={page.pageNum}
                  cssWidth={cssWidth}
                  title={title}
                />
              </figure>
            ))
          : null}
      </div>
      {!loading && !error && pages.length > 0 && !showPaperSize ? (
        <p className="mt-2 text-center text-[11px] text-label-tertiary">
          {pages.length === 1 ? "1 Seite" : `${pages.length} Seiten`}
        </p>
      ) : null}
    </div>
  );
}

function PdfJsPageCanvas({
  pdf,
  pageNum,
  cssWidth,
  title,
}: {
  pdf: PdfDocument;
  pageNum: number;
  cssWidth: number;
  title: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<void> } | null = null;

    async function render() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const page = await pdf.getPage(pageNum);
      if (cancelled) return;
      const unscaled = page.getViewport({ scale: 1 });
      const scale = cssWidth / unscaled.width;
      const viewport = page.getViewport({ scale });
      const outputScale = Math.min(window.devicePixelRatio || 1, 2);
      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(outputScale, 0, 0, outputScale, 0, 0);
      task = page.render({ canvasContext: ctx, viewport });
      await task.promise;
    }

    void render().catch(() => {
      /* Fehler zeigt die übergeordnete Seite */
    });

    return () => {
      cancelled = true;
      try {
        task?.cancel();
      } catch {
        /* ignore */
      }
    };
  }, [pdf, pageNum, cssWidth]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full rounded-lg border border-separator bg-white shadow-sm"
      aria-label={`${title} – Seite ${pageNum}`}
    />
  );
}
